import { Injectable } from '@angular/core';
import { TerminalAtivacaoResponse } from '../../api.service';
import { ServicoTenant } from '../tenant/servico-tenant';

export interface EstadoTerminalLocal {
  slugTenant: string;
  dispositivoId: string;
  nome: string;
  tokenTerminal: string;
  dataHoraServidor: string;
  autorizacaoOfflineExpiraEm: string;
  zoomCamera?: number;
  areaLeituraCameraPercentual?: number;
  validadoEmLocalMs: number;
}

export interface AvaliacaoTerminalOffline {
  permitido: boolean;
  motivo?: string;
  estado?: EstadoTerminalLocal;
}

@Injectable({ providedIn: 'root' })
export class ServicoEstadoTerminalLocal {
  private readonly margemRelogioMs = 10 * 60 * 1000;

  constructor(private readonly servicoTenant: ServicoTenant) {}

  registrarValidacao(terminal: TerminalAtivacaoResponse, tokenTerminal: string) {
    const slugTenant = this.servicoTenant.slugTenantAtual;
    if (!slugTenant || !terminal.dispositivoId || !tokenTerminal) {
      return;
    }

    const agora = new Date();
    const dataHoraServidor = terminal.dataHoraServidor || agora.toISOString();
    const autorizacaoOfflineExpiraEm = terminal.autorizacaoOfflineExpiraEm
      || new Date(agora.getTime() + (terminal.validadeOfflineMinutos || 720) * 60 * 1000).toISOString();

    const estado: EstadoTerminalLocal = {
      slugTenant,
      dispositivoId: terminal.dispositivoId,
      nome: terminal.nome,
      tokenTerminal,
      dataHoraServidor,
      autorizacaoOfflineExpiraEm,
      zoomCamera: terminal.zoomCamera,
      areaLeituraCameraPercentual: terminal.areaLeituraCameraPercentual,
      validadoEmLocalMs: Date.now()
    };

    localStorage.setItem(this.chaveEstado, JSON.stringify(estado));
  }

  avaliarOperacaoOffline(tokenTerminal: string): AvaliacaoTerminalOffline {
    const estado = this.estadoAtual();
    if (!estado) {
      return { permitido: false, motivo: 'Terminal sem validacao online recente.' };
    }

    if (estado.slugTenant !== this.servicoTenant.slugTenantAtual || estado.tokenTerminal !== tokenTerminal) {
      return { permitido: false, motivo: 'Terminal local nao pertence a este tenant.', estado };
    }

    const agoraLocalMs = Date.now();
    if (agoraLocalMs + this.margemRelogioMs < estado.validadoEmLocalMs) {
      return { permitido: false, motivo: 'Relogio local divergente. Conecte a internet para validar o terminal.', estado };
    }

    const dataHoraExpiracaoMs = Date.parse(estado.autorizacaoOfflineExpiraEm);
    const dataHoraServidorMs = Date.parse(estado.dataHoraServidor);
    const dataHoraServidorProjetadaMs = dataHoraServidorMs + Math.max(0, agoraLocalMs - estado.validadoEmLocalMs);

    if (!Number.isFinite(dataHoraExpiracaoMs) || !Number.isFinite(dataHoraServidorProjetadaMs)) {
      return { permitido: false, motivo: 'Estado local do terminal invalido.', estado };
    }

    if (dataHoraServidorProjetadaMs > dataHoraExpiracaoMs) {
      return { permitido: false, motivo: 'Autorizacao offline do terminal expirada. Conecte a internet para validar novamente.', estado };
    }

    return { permitido: true, estado };
  }

  estadoAtual(): EstadoTerminalLocal | null {
    const bruto = localStorage.getItem(this.chaveEstado);
    if (!bruto) {
      return null;
    }

    try {
      return JSON.parse(bruto) as EstadoTerminalLocal;
    } catch {
      this.limpar();
      return null;
    }
  }

  limpar() {
    localStorage.removeItem(this.chaveEstado);
  }

  private get chaveEstado() {
    const slugTenant = this.servicoTenant.slugTenantAtual || 'global';
    return `pontojwm.terminal.estado.${slugTenant}`;
  }
}
